import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Pagination } from 'transfix-ui/components/Pagination'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Table, TableBody } from 'transfix-ui/components/Table'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useValidationFormContext } from 'utils/context/ValidationFormContext'
import { DEFAULT_PER_PAGE } from 'apps/carrier/constants/carrierProfile'
import { ResponsiveLayout } from 'apps/fleet-planner/components/layout/ResponsiveLayout'
import { ResponsiveLayoutRow } from 'apps/fleet-planner/components/layout/ResponsiveLayoutRow'
import { FP_SUPPORT_EMAIL } from 'apps/fleet-planner/config'
import { useFacilityApi, useFMSAnalytics } from 'apps/fleet-planner/hooks'
import {
  IFacilityFilters,
  SortFieldType,
  SortOrderType,
  useFacilityParams,
} from 'apps/fleet-planner/hooks/useFacilityParams'
import {
  FacilitySchemaType,
  createNewFacilityRequest,
  IExistingFacility,
  createUpdateFacilityRequest,
} from 'apps/fleet-planner/schema'
import * as Types from 'apps/fleet-planner/types'
import {
  createNewFacilitySuccess,
  updateFacilitySuccess,
} from 'apps/fleet-planner/utils/actions/facilities'
import { IFacility } from 'apps/fleet-planner/utils/api'
import { getFacilityDefaultValues } from 'apps/fleet-planner/utils/getFacilityDefaultValues'
import { AddIcon } from 'transfix-icons'
import { FacilityForm } from '../forms/FacilityForm'
import { AddEntityModal } from '../modals'
import { DeleteFacilityConfirmation } from './DeleteFacilityConfirmation'
import { FacilitesSortingOptions } from './FacilitesSortingOptions'
import { FacilitiesFilters } from './FacilitiesFilters'
import { FacilitiesMobileRow } from './FacilitiesMobileRow'
import { FacilitiesTableHeader } from './FacilitiesTableHeader'
import { FacilitiesTableRow } from './FacilitiesTableRow'

const getDefaultEditFacilityState = (
  facility: IFacility | undefined
): IExistingFacility | undefined => {
  if (facility === undefined) {
    return undefined
  }

  const { addresses = [] } = facility || {}
  const [firstAddress] = addresses || []
  const {
    addressLine1 = '',
    addressLine2 = '',
    addressee = '',
    id: addressId = '',
    locality = '',
    postalCode = '',
    subdivisionCode = '',
  } = firstAddress || {}

  const { contacts = [] } = facility || {}
  const [firstContact] = contacts || []
  const { email = '', id: contactId = 0, name: contactName = '', phone = '', phoneExt = '' } =
    firstContact || []

  return {
    id: facility.id,
    name: facility.name ?? '',
    address: {
      addressee,
      addressLine1,
      addressLine2,
      postalCode,
      location: {
        subdivisionCode: subdivisionCode ?? '',
        locality: locality ?? '',
        id: addressId,
      },
    },
    contact: {
      id: contactId,
      name: contactName ?? '',
      email: email ?? '',
      phone: phone ?? '',
      phoneExt: phoneExt ?? '',
    },
  }
}

export const FacilitiesPage = () => {
  const { trackEvent } = useFMSAnalytics()
  const { params: facilityParams, setParams: setFacilityParams } = useFacilityParams()
  const {
    createNewFacility,
    deleteFacility,
    error: apiError,
    facilitiesList,
    getFacilities,
    isLoading: isFacilitiesListLoading,
    resetError,
    totalCount,
    updateFacility,
  } = useFacilityApi()
  const { pathname = '' } = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { isMobile } = useBreakpointContext()
  const facilitiesBulkUploadClickHandler = () =>
    trackEvent('Bulk Upload', 'CLICK_FACILITIES_BULK_UPLOAD_LINK')

  const isAddFacilityModalOpen = pathname.includes('facilities/create-facility')
  const isEditFacilityModalOpen = pathname.includes('facilities/edit-facility')
  const stop: Partial<Types.IStop> = {}

  // Fetch facilities when params are updated.
  useEffect(() => {
    if (!isFacilitiesListLoading) {
      getFacilities(facilityParams)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityParams, getFacilities])

  const [defaultFacilityName] = useState<string>('')
  const [formStop, setFormStop] = useState<Partial<Types.IStop>>(stop)
  const [currentFacility, setCurrentFacility] = useState<IFacility>()
  const [lastUpdatedFacilityId, setLastUpdatedFacilityId] = useState<string | undefined>(undefined)

  const {
    errors: facilityFormErrors,
    setHasErrors: setHasFacilityFormErrors,
  } = useValidationFormContext()
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const [isShowingDeleteConfirmation, showDeleteConfirmation] = useState<boolean>(false)

  const clickAddFacilityHandler = () => {
    history.push('/facilities/create-facility')
  }

  const submitAddFacilityHandler = async (data: FacilitySchemaType) => {
    const req = createNewFacilityRequest(data)
    const res = await createNewFacility(req)

    if (res) {
      dispatch(createNewFacilitySuccess(res))
      getFacilities(facilityParams)
      history.push('/facilities')
      openToast({
        toastType: 'default',
        toastMessage: 'Facility added.',
      })
      setLastUpdatedFacilityId(res.id)
    }
    setHasFacilityFormErrors(false)
  }

  const submitUpdateFacilityHandler = async (data: FacilitySchemaType) => {
    const req = createUpdateFacilityRequest({ id: currentFacility?.id ?? '', ...data })
    const res = await updateFacility(req)

    if (res) {
      dispatch(updateFacilitySuccess(res))
      getFacilities(facilityParams)
      history.push('/facilities')
      openToast({
        toastType: 'default',
        toastMessage: 'Facility updated.',
      })
      setLastUpdatedFacilityId(currentFacility?.id)
    }
    setHasFacilityFormErrors(false)
  }

  const onPageChange = (pageNumber: number) => {
    setFacilityParams(prevParams => ({
      ...prevParams,
      pageNumber,
    }))
  }

  const submitFacilityForm = async (data: FacilitySchemaType) => {
    if (currentFacility?.id) {
      await submitUpdateFacilityHandler(data)
    } else {
      await submitAddFacilityHandler(data)
    }
  }

  const cancelAddFacilityHandler = () => {
    history.push('/facilities')
    setFormStop(stop)
    setHasFacilityFormErrors(false)
    resetError()
  }

  const facilityDefaultValues = useMemo(
    () =>
      getFacilityDefaultValues(
        formStop.facility?.name || defaultFacilityName,
        formStop.address,
        formStop.facilityContact
      ),
    [defaultFacilityName, formStop.address, formStop.facility?.name, formStop.facilityContact]
  )
  const filterChangeHandler = (filters: IFacilityFilters) => {
    setFacilityParams(prevParams => ({
      ...prevParams,
      filters,
    }))

    setLastUpdatedFacilityId(undefined)
  }

  const sortFieldHandler = (sortField?: SortFieldType) => {
    setFacilityParams(prevParams => ({
      ...prevParams,
      sortField,
      sortOrder: prevParams.sortOrder === 'desc' ? 'asc' : 'desc',
    }))
  }

  const mobileSortFieldHandler = (sortOrder?: SortOrderType) => {
    setFacilityParams(prevParams => ({
      ...prevParams,
      sortOrder,
    }))
  }

  const clickEditFacilityHandler = (facility: IFacility) => {
    history.push(`/facilities/edit-facility/${facility.id}`)
    setCurrentFacility(facility)
  }

  const deleteFacilityHandler = (facility: IFacility) => {
    setCurrentFacility(facility)
    showDeleteConfirmation(true)
  }

  const deleteConfirmHandler = () => {
    if (!currentFacility) {
      showDeleteConfirmation(false)
      return
    }
    deleteFacility(currentFacility.id)
      .then(() => {
        getFacilities(facilityParams)
        openToast({
          toastType: 'default',
          toastMessage: 'Facility deleted successfully.',
        })
      })
      .catch(() => {
        openToast({
          toastType: 'alert',
          toastMessage: 'Failed to delete facility.',
        })
      })
      .finally(() => {
        showDeleteConfirmation(false)
      })
  }

  const cancelEditFacilityHandler = () => {
    history.push('/facilities')
    setCurrentFacility(undefined)
    setHasFacilityFormErrors(false)
    resetError()
  }

  // keeps the last updated or newly added facility on top
  const sortFacilities = (): IFacility[] => {
    if (!lastUpdatedFacilityId) {
      return facilitiesList
    }
    const lastUpdatedFacility = facilitiesList.find(i => i.id === lastUpdatedFacilityId)
    if (lastUpdatedFacility) {
      return [
        lastUpdatedFacility,
        ...facilitiesList.filter(item => item.id !== lastUpdatedFacilityId),
      ]
    }

    return facilitiesList
  }

  const sortedFacilities = sortFacilities()
  return (
    <ResponsiveLayout
      title='Facilities'
      primaryActionProps={{
        label: 'Add Facility',
        fabIcon: AddIcon,
        onClick: clickAddFacilityHandler,
      }}>
      <ResponsiveLayoutRow>
        <FacilitiesFilters params={facilityParams} onFilterChange={filterChangeHandler} />
      </ResponsiveLayoutRow>

      {isShowingDeleteConfirmation && (
        <DeleteFacilityConfirmation
          onDelete={deleteConfirmHandler}
          onCancel={() => showDeleteConfirmation(false)}
        />
      )}

      {isMobile && (
        <ResponsiveLayoutRow BoxProps={{ pt: 2 }}>
          <FacilitesSortingOptions onChange={mobileSortFieldHandler} />
        </ResponsiveLayoutRow>
      )}

      {isFacilitiesListLoading && (
        <ResponsiveLayoutRow fluid>
          <Spinner position='relative' />
        </ResponsiveLayoutRow>
      )}

      {!isFacilitiesListLoading && (
        <ResponsiveLayoutRow BoxProps={{ pt: 2 }} fluid>
          <Table>
            {isMobile ? (
              <></>
            ) : (
              <FacilitiesTableHeader params={facilityParams} onChange={sortFieldHandler} />
            )}
            <TableBody>
              {sortedFacilities.map((facility: IFacility) =>
                isMobile ? (
                  <FacilitiesMobileRow
                    key={facility.id}
                    facility={facility}
                    onEditRow={clickEditFacilityHandler}
                  />
                ) : (
                  <FacilitiesTableRow
                    key={facility.id}
                    facility={facility}
                    isUpdated={facility.id === lastUpdatedFacilityId}
                    onEditRow={clickEditFacilityHandler}
                    onDeleteRow={deleteFacilityHandler}
                  />
                )
              )}
            </TableBody>
          </Table>
          <div className='tc'>
            {Math.ceil(totalCount / DEFAULT_PER_PAGE) > 1 && (
              <Pagination
                currentPage={facilityParams.pageNumber}
                numberOfPages={Math.ceil(totalCount / DEFAULT_PER_PAGE)}
                onPageChange={onPageChange}
              />
            )}
          </div>
        </ResponsiveLayoutRow>
      )}

      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />

      {isAddFacilityModalOpen && (
        <AddEntityModal
          open
          title='Add New Facility'
          error={apiError || facilityFormErrors}
          onClose={cancelAddFacilityHandler}
          dataTest='add-new-facility-modal'>
          <InlineMessage
            className='mb5'
            message={
              <span>
                <b>Lots of facilities to add?</b>{' '}
                <a href={`mailto:${FP_SUPPORT_EMAIL}`} onClick={facilitiesBulkUploadClickHandler}>
                  Contact us
                </a>{' '}
                and we’ll import your facilities in bulk.
              </span>
            }
          />
          <FacilityForm
            defaultValues={facilityDefaultValues}
            onSuccess={submitFacilityForm}
            onCancel={cancelAddFacilityHandler}
            setHasErrors={setHasFacilityFormErrors}
          />
        </AddEntityModal>
      )}
      {isEditFacilityModalOpen && (
        <AddEntityModal
          open
          title='Edit Facility'
          error={apiError || facilityFormErrors}
          onClose={cancelEditFacilityHandler}
          dataTest='add-edit-facility-modal'>
          <FacilityForm
            defaultValues={getDefaultEditFacilityState(currentFacility)}
            onSuccess={submitFacilityForm}
            onCancel={cancelEditFacilityHandler}
            setHasErrors={setHasFacilityFormErrors}
          />
        </AddEntityModal>
      )}
    </ResponsiveLayout>
  )
}
